import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Services = () => {
  const services = [
    {
      id: 'web',
      title: 'Web Development',
      description: 'We create modern, responsive websites that help your business grow online.',
      features: [
        'Static websites for maximum performance',
        'WordPress development and customization',
        'Python-based web applications',
        'E-commerce solutions',
        'Content Management Systems',
        'SEO optimization',
      ],
      icon: '🌐',
    },
    {
      id: 'custom',
      title: 'Custom Software Development',
      description: 'Tailored software solutions designed to solve your specific business challenges.',
      features: [
        'Business process automation',
        'Custom CRM development',
        'Enterprise software solutions',
        'API development and integration',
        'Legacy system modernization',
        'Mobile app development',
      ],
      icon: '💻',
    },
    {
      id: 'cloud',
      title: 'Cloud Infrastructure Development',
      description: 'Scalable and secure cloud solutions to power your digital transformation.',
      features: [
        'AWS infrastructure setup and management',
        'Cloud migration services',
        'DevOps implementation',
        'Container orchestration',
        'Serverless architecture',
        'Cloud security implementation',
      ],
      icon: '☁️',
    },
  ];

  const faqs = [
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on complexity and requirements. A basic website might take 4-6 weeks, while complex custom software solutions can take 3-6 months or more.'
    },
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes, we offer various support packages to ensure your solution continues to perform optimally. This includes regular maintenance, updates, and technical support.'
    },
    {
      question: 'What is your development process?',
      answer: 'We follow an agile development process, starting with discovery and planning, followed by iterative development cycles with regular client feedback and testing.'
    },
    {
      question: 'Can you help with existing systems?',
      answer: 'Absolutely! We specialize in modernising legacy systems and can help migrate or upgrade your existing infrastructure to more efficient solutions.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services | nautdigital</title>
        <meta name="description" content="Comprehensive digital solutions to help your business thrive. Explore our web development, custom software, and cloud services." />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden">
        {/* Top gradient overlay, full width */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-dark-lighter to-black pointer-events-none z-0" />
        
        <div className="container mx-auto pt-32 pb-16 px-4 relative z-10">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h1 className="text-5xl font-bold text-white mb-4">Our Services</h1>
            <p className="text-xl text-gray-300 max-w-5xl mx-auto">
              We offer comprehensive digital solutions to help your business thrive in the modern world.
            </p>
          </motion.div>

          {/* Services List - New Card Layout */}
          <div className="max-w-6xl mx-auto space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative overflow-hidden bg-dark-lighter/60 backdrop-blur-lg border border-white/10 shadow-xl rounded-2xl p-8 md:p-12"
              >
                <div className="absolute -top-16 -left-16 w-48 h-48 bg-accent/10 rounded-full blur-3xl opacity-70 pointer-events-none" />
                
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="text-7xl mb-4 md:mb-0">{service.icon}</div>
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-3xl font-bold text-white mb-3">
                      {service.title}
                    </h2>
                    <p className="text-gray-300 text-lg mb-6">
                      {service.description}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-gray-300">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center gap-3 justify-center md:justify-start">
                          <span className="text-primary">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Process CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden mt-24 text-center bg-primary p-12 rounded-2xl shadow-lg"
          >
            <div className="absolute -top-20 -right-20 w-48 h-48 bg-white/10 rounded-full blur-3xl opacity-60 pointer-events-none" />
            <h2 className="relative z-10 text-3xl font-bold text-white mb-6">
              Curious About Our Methods?
            </h2>
            <p className="relative z-10 text-gray-200 text-lg max-w-4xl mx-auto mb-8">
              Our transparent, collaborative process is the key to our success. See how we turn ideas into reality.
            </p>
            <div className="relative z-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/process"
                className="inline-block px-8 py-4 bg-white text-primary rounded-lg font-medium transition-all duration-300 hover:bg-gray-200"
              >
                Discover Our Process
              </Link>
              <Link
                to="/contact#contact-form"
                className="inline-block px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-medium transition-all duration-200 hover:bg-white hover:text-primary"
              >
                Get a Custom Quote
              </Link>
            </div>
          </motion.div>

          {/* FAQs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-24"
          >
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="grid gap-6 max-w-5xl mx-auto">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-dark-lighter rounded-lg p-6"
                >
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-300">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Services; 